import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Inbox, AlertCircle, Loader2 } from 'lucide-react';
import './GlobalEmployeeSearch.css';
import config from '../../configLoader';
import { useSiteContext } from '../../SiteContext';
import EmployeeManager from '../../managers/EmployeeManager';
import { Page } from '../../interfaces/PagesInterface';
import { EmployeeSearchResult } from '../../interfaces/EmployeeInterface';
import { can, canEditEntries, canSubmitForEmployee } from '../../util/adminPermissions';
import { mapSearchStatus } from '../../util/employeeSearchStatus';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

/**
 * The header's global employee search. Lives in the top bar so an employee lookup works
 * from any v2 page — it replaces the page-local search boxes that used to sit on Home
 * and on the Timesheet Review board.
 *
 * Follows the GlobalWeekPill pattern: no state props, everything from SiteContext.
 * Picking a result sets a ReviewIntent and navigates to the board, which is the same
 * handoff Home's search box used; the board's intent consumer is re-entrant, so this
 * works even when the board is the page you are already on.
 */

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
/** Below this the field collapses to a magnifier — matches the shell.css breakpoint. */
const COMPACT_BP = 1100;

interface GlobalEmployeeSearchProps {
  /**
   * Lets the top bar raise its own z-index while the results are up. The results
   * overlay is portalled to <body> (it has to be — see GlobalEmployeeSearch.css), so
   * without this the search field would sit UNDER its own backdrop.
   */
  onResultsOpenChange?: (open: boolean) => void;
}

const GlobalEmployeeSearch: React.FC<GlobalEmployeeSearchProps> = ({ onResultsOpenChange }) => {
  const { userRole, payPeriodID, week, setPage, setReviewIntent } = useSiteContext();

  const [term, setTerm] = useState('');
  const [results, setResults] = useState<EmployeeSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  // Monotonic request id. As-you-type responses land out of order and there is no
  // AbortSignal threaded through EmployeeManager.searchEmployees, so the newest
  // request wins by sequence number (same approach as payrollEdit/ProjectPicker).
  const seqRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const debounced = useDebouncedValue(term, DEBOUNCE_MS);

  // The same gate App.tsx puts on the route — never offer a search whose only
  // destination is a page this viewer cannot open.
  const enabled = config.showTimesheetReviewPage === true && can(userRole, 'viewTimesheets');

  // Zero-entry gate (Payroll Timesheet Modifications): a "no timesheet" hit is openable
  // when the tenant runs payroll editing AND the viewer holds a write permission.
  const canOpenZeroEntry = config.payrollTimesheetEditing?.enabled === true
    && (canEditEntries(userRole) || canSubmitForEmployee(userRole));

  useEffect(() => { onResultsOpenChange?.(open); }, [open, onResultsOpenChange]);

  const runSearch = useCallback(async (q: string, ppID: number) => {
    const seq = ++seqRef.current;
    setLoading(true);
    setFailed(false);
    setAttempted(true);
    setOpen(true);
    try {
      // Approval Access (WP8): the server scopes results from the MSAL session.
      const response = await EmployeeManager.searchEmployees(q, ppID);
      if (seq !== seqRef.current) return; // a newer keystroke already won
      const rows: EmployeeSearchResult[] = response?.data ?? [];
      setResults(rows);
      setHighlight(rows.length > 0 ? 0 : -1);
    } catch (err) {
      if (seq !== seqRef.current) return;
      console.error('Error searching employees:', err);
      setResults([]);
      setHighlight(-1);
      setFailed(true);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  // The fetch is an effect, not an onChange call, and that is load-bearing twice over:
  // a query typed before pay periods resolve fires by itself the moment payPeriodID
  // lands, and changing the week re-runs the live query — timesheetStatus is per pay
  // period, so a stale pill would route to the wrong bucket.
  useEffect(() => {
    if (!enabled) return;
    const q = debounced.trim();
    if (q.length < MIN_CHARS) {
      seqRef.current++; // invalidate anything in flight
      setResults([]);
      setHighlight(-1);
      setLoading(false);
      setFailed(false);
      setAttempted(false);
      setOpen(false);
      return;
    }
    if (!payPeriodID) return; // week not resolved yet — the effect re-runs when it is
    runSearch(q, payPeriodID);
  }, [debounced, payPeriodID, enabled, runSearch]);

  // Invalidate in-flight work on unmount; the debounce timer cleans itself up.
  useEffect(() => () => { seqRef.current++; }, []);

  const close = useCallback(() => {
    setOpen(false);
    setHighlight(-1);
  }, []);

  const reset = useCallback(() => {
    seqRef.current++;
    setTerm('');
    setResults([]);
    setOpen(false);
    setExpanded(false);
    setHighlight(-1);
    setAttempted(false);
    setFailed(false);
    setLoading(false);
  }, []);

  const isClickable = useCallback(
    (r: EmployeeSearchResult) => mapSearchStatus(r.timesheetStatus) !== null || canOpenZeroEntry,
    [canOpenZeroEntry]
  );

  const pick = useCallback((r: EmployeeSearchResult) => {
    const mapped = mapSearchStatus(r.timesheetStatus);
    if (!mapped && !canOpenZeroEntry) return;
    // `name` is load-bearing: the board's openEmployeeIntent falls back to a synthesized
    // single-item queue built from it, and opens NOTHING when it is missing and the
    // employee isn't in a loaded bucket.
    setReviewIntent({
      bucket: mapped?.bucket ?? 'unsubmitted',
      employeeID: r.employeeID,
      altEmployeeID: r.altEmployeeID,
      name: r.name,
    });
    reset();
    setPage(Page.TimesheetReview);
  }, [canOpenZeroEntry, reset, setPage, setReviewIntent]);

  // Escape closes the results first, then collapses a compact field. Bound to the window
  // so it works whether focus is in the input or on a result row.
  useEffect(() => {
    if (!open && !expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (open) close();
      else setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, expanded, close]);

  // Keep the highlighted row visible inside the list, without scrolling the document.
  useEffect(() => {
    if (!open || highlight < 0) return;
    const list = listRef.current;
    const el = list?.querySelector('.ges-row.hl') as HTMLElement | null;
    if (!list || !el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [highlight, open, results]);

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!results.length) return;
      e.preventDefault(); // don't scroll the page or move the caret
      setHighlight((h) => {
        const n = results.length;
        return e.key === 'ArrowDown' ? (h + 1 + n) % n : (h - 1 + n) % n;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const q = term.trim();
      if (open && highlight >= 0 && results[highlight]) {
        pick(results[highlight]);
      } else if (q.length >= MIN_CHARS && payPeriodID) {
        runSearch(q, payPeriodID); // flush ahead of the debounce
      }
    } else if (e.key === 'Tab') {
      close();
    }
  };

  const openCompact = () => {
    setExpanded(true);
    // The field only exists once expanded renders, hence the tick.
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (!enabled) return null;

  const overlay = open ? ReactDOM.createPortal(
    <div className="ges-modal-bg" onMouseDown={close}>
      <div className="ges-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ges-head">
          <span className="mi"><Search /></span>
          <div className="ges-head-txt">
            <h3>Employee Search Results{loading ? '' : ` (${results.length})`}</h3>
            <div className="ms">Week {week} · “{term.trim()}”</div>
          </div>
          <button className="x" onClick={close} type="button" aria-label="Close search results">
            <X />
          </button>
        </div>
        <div className="ges-body" ref={listRef}>
          {loading ? (
            <div className="ges-state"><Loader2 className="spin" /><div className="et">Searching…</div></div>
          ) : failed ? (
            <div className="ges-state">
              <AlertCircle />
              <div className="et">Search failed</div>
              <p>Could not reach the server. Keep typing to try again.</p>
            </div>
          ) : results.length > 0 ? (
            results.map((r, i) => {
              const mapped = mapSearchStatus(r.timesheetStatus);
              const clickable = isClickable(r);
              return (
                <button
                  key={`${r.employeeID}-${i}`}
                  type="button"
                  className={`ges-row${clickable ? '' : ' disabled'}${i === highlight ? ' hl' : ''}`}
                  onClick={() => clickable && pick(r)}
                  onMouseEnter={() => setHighlight(i)}
                >
                  <div className="ges-main">
                    <div className="ges-nm">
                      {r.name.first} {r.name.last}
                      <span className="ges-id">#{r.altEmployeeID}</span>
                    </div>
                    <div className="ges-loc">{r.location?.name || '—'}</div>
                  </div>
                  {mapped ? (
                    <span className={`ges-pill ${mapped.variant}`}>{mapped.label}</span>
                  ) : (
                    <span className="ges-pill neutral">
                      {canOpenZeroEntry ? 'No entries' : 'No Timesheet for Week'}
                    </span>
                  )}
                </button>
              );
            })
          ) : (
            <div className="ges-state">
              <Inbox />
              <div className="et">No matches</div>
              <p>
                {attempted
                  ? `No employee matches “${term.trim()}” for Week ${week}.`
                  : 'No employees found matching your search.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={`ges${expanded ? ' expanded' : ''}`}>
      {/* Compact (<1100px) trigger. Hidden by CSS at wider widths, where the field shows. */}
      <button
        type="button"
        className="ges-toggle"
        onClick={openCompact}
        title="Search employees"
        aria-label="Search employees"
      >
        <Search />
      </button>

      <div className="ges-field">
        <Search className="ges-fic" />
        <input
          ref={inputRef}
          type="text"
          value={term}
          placeholder="Search employee ID or name"
          aria-label="Search employee ID or name"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onInputKey}
          onFocus={() => { if (results.length || loading) setOpen(true); }}
          onBlur={() => { if (window.innerWidth < COMPACT_BP && !term.trim() && !open) setExpanded(false); }}
        />
        {term !== '' && (
          <button type="button" className="ges-clear" onClick={reset} aria-label="Clear search">
            <X />
          </button>
        )}
      </div>

      {overlay}
    </div>
  );
};

export default GlobalEmployeeSearch;
